if (!customElements.get("quick-cart-drawer")) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", this.init.bind(this));
        this.parentElement.addEventListener("shopify:section:select", () => this.open());
        this.parentElement.addEventListener("shopify:section:deselect", () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains("is--open")) {
        document.querySelector("body").classList.remove("overflow-hidden");
      }

      this.querySelector(".button--close").addEventListener("click", this.close.bind(this));
      this.querySelector(".quick-cart-drawer__backdrop").addEventListener("click", this.close.bind(this));

      this.querySelector(".quick-cart-drawer__blocks").addEventListener("keydown", e => {
        if (e.key === "Escape" && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];
          let isTabPressed = e.key === "Tab";

          if (!isTabPressed) return;

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      const observer = new MutationObserver(() => {
        document.querySelectorAll(".quick-cart-drawer__trigger").forEach(element => {
          if (!element._init) {
            element.addEventListener("click", event => {
              this.fetchProductForQuickCartDrawer(event);
            });
            element._init = true;
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    async fetchProductForQuickCartDrawer(event) {
      if (event.target.closest("card-product-slider")) {
        event.target.closest("card-product-slider").slider.autoplay.stop();
        event.target.closest("card-product-slider").classList.add("product--open-on-quick-cart");
      }

      const isRecommendations = event.target.classList.contains("quick-cart-drawer__trigger--recommendations");
      const productCard = event.target.closest("product-card");
      let productOptions = null;

      if (!isRecommendations && productCard) {
        productOptions = productCard.querySelectorAll('input[type="radio"]:checked, select');
      }

      const trigger = event.target.closest("[data-product-url]");
      trigger.classList.toggle("is--loading");
      try {
        const res = await fetch(`${trigger.dataset.productUrl}/?view=quick-cart`);
        const html = await res.text();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const quickCartProduct = tempDiv.querySelector(".quick-cart-product");
        if (!quickCartProduct) return;

        this.querySelector(".quick-cart-drawer__main").innerHTML = "";
        this.querySelector(".quick-cart-drawer__main").append(quickCartProduct);

        this.sliderInstance = new Swiper(".quick-cart-drawer__media-swiper", {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: { enabled: true },
          breakpoints: {
            750: { slidesPerView: 2 }
          }
        });

        if (!isRecommendations && productOptions) {
          productOptions.forEach(opt => {
            const drawerOpt = this.querySelector(`[name="${opt.name}"][value="${opt.value}"]`);
            if (!drawerOpt) return;
            drawerOpt.setAttribute("checked", "");
            drawerOpt.closest("li").classList.add("checked");
            const label = drawerOpt.parentElement.parentElement.querySelector("legend [data-selected-variant]");
            if (label) label.textContent = opt.value;
          });

          this.querySelector("product-card input[name='id']").value =
            tempDiv.querySelector("input[name='id']").value;

          this.querySelector("product-card").init();
        }

        setTimeout(() => this.open(), 500);

        this.querySelectorAll(".variant-option-radio-input").forEach(input => {
          input.addEventListener("change", e => {
            const label = e.target.parentElement.parentElement.querySelector("legend [data-selected-variant]");
            if (label) label.textContent = e.target.value;

            const selectedId = this.querySelector(".product-card__add-to-cart--form input[name='id']").value;
            const variants = this.querySelector("product-card").variantsObj;
            const featuredMedia = variants.find(v => v.id == selectedId)?.featured_media;
            if (featuredMedia) this.setActiveMedia(featuredMedia.id);
          });
        });
      } catch (e) {
        console.error(e);
      } finally {
        trigger.classList.toggle("is--loading");
      }
    }

    setActiveMedia(id) {
      const media = Array.from(this.querySelectorAll("[data-media-id]")).find(m => Number(m.dataset.mediaId) === id);
      if (media) this.sliderInstance.slideTo(Number(media.dataset.index));
    }

    toggle() {
      this.toggleState ? this.close() : this.open();
    }

    open() {
      this.toggleState = true;
      document.body.classList.add("overflow-hidden");
      this.querySelector(".quick-cart-drawer__blocks").setAttribute("tabindex", "0");
      this.querySelector(".button--close").setAttribute("tabindex", "0");
      this.classList.add("is--open");
      this.opened();
      const focusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    }

    close() {
      this.toggleState = false;
      const shopLookDrawer = document.querySelector("shop-the-look-drawer");
      if (!shopLookDrawer || !shopLookDrawer.classList.contains("is--open")) {
        document.body.classList.remove("overflow-hidden");
      }
      this.classList.remove("is--open");
      this.closed();
      this.toggleAriaExpanded();
      const slider = document.querySelector("card-product-slider.product--open-on-quick-cart");
      if (slider) {
        slider.slider.autoplay.start();
        slider.classList.remove("product--open-on-quick-cart");
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        const btn = event.target.closest("button");
        if (btn) btn.setAttribute("aria-expanded", true);
        this.querySelector(".button--close").setAttribute("aria-expanded", true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(btn =>
          btn.setAttribute("aria-expanded", false)
        );
      }
    }

    opened() {
      this.dispatchEvent(new Event("opened", { bubbles: true }));
    }

    closed() {
      this.dispatchEvent(new Event("closed", { bubbles: true }));
    }
  }

  customElements.define("quick-cart-drawer", QuickCartDrawer);
}

// ========= 实时免运费提示功能 =========
(function () {
  const threshold = 5000 * 100; // ¥5,000
  const priceSelector = '.cart-drawer__total-price';
  const messageEl = document.getElementById('free-shipping-message');
  if (!messageEl) return;

  const renderMessage = () => {
    const priceNode = document.querySelector(priceSelector);
    if (!priceNode) return;
    const subtotal = parseInt(priceNode.dataset.cartTotalPrice, 10);
    if (subtotal >= threshold) {
      messageEl.innerHTML = 'おめでとうございます！ご注文は<strong>送料無料</strong>の対象です。';
    } else {
      const remain = (threshold - subtotal) / 100;
      messageEl.innerHTML = `あと<strong>${remain.toLocaleString()}円</strong>で<strong>送料無料</strong>になります。`;
    }
  };

  renderMessage();
  document.addEventListener('cart:updated', renderMessage);
  window.updateFreeShipping = renderMessage;
})();
